import React, { useState, useMemo } from 'react';
import Seo from '../../components/Seo';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Globe, Shield, Zap, CheckCircle2, TrendingUp, Users, Search } from 'lucide-react';
import { useCountries } from '../../hooks/useCountries';
import NumberCountryTable, { ResultsLine, matchesCountry } from '../../components/numbers/NumberCountryTable';

const NationalNumbers = () => {
  const { countries, loading } = useCountries();
  const nationalCountries = countries.filter(c => c.national);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return nationalCountries;
    return nationalCountries.filter((c) => matchesCountry(c, searchTerm));
  }, [searchTerm, nationalCountries]);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="National Phone Numbers for Country-Wide Reach"
        description="Nationwide non-geographic phone numbers for a country-wide presence, not tied to one city or region."
        path="/phone-numbers/national"
        breadcrumbs={[{ name: 'Phone Numbers', path: '/phone-numbers/all' }, { name: 'National Numbers', path: '/phone-numbers/national' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
                National Phone Numbers
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Expand your reach with national numbers that cover entire countries. Perfect for businesses serving nationwide markets without geographic limitations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
                  className="hover:opacity-90 font-semibold"
                  onClick={() => window.location.href = '/phone-numbers/all'}
                >
                  View All Coverage
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] rounded-lg p-6 sm:p-12 text-white">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>{nationalCountries.length}+</div>
                  <div className="text-sm mt-2">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>Nationwide</div>
                  <div className="text-sm mt-2">Coverage</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>99.99%</div>
                  <div className="text-sm mt-2">Uptime SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-4xl font-bold" style={{ color: '#c8ff00' }}>&lt;1min</div>
                  <div className="text-sm mt-2">Activation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#0a1f2e' }}>
            National Numbers Available in {nationalCountries.length} Countries
          </h2>
          <Card className="p-4 sm:p-6">
            {/* Search Filter */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search by country name or prefix (e.g., Germany or +49)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-6 text-base"
                />
              </div>
              <div className="flex items-center justify-between gap-3 mt-3">
                <ResultsLine count={filteredCountries.length} total={nationalCountries.length} loading={loading} />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-sm text-[#c8ff00] hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            </div>
            
            <NumberCountryTable countries={filteredCountries} group="national" />

            {filteredCountries.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No countries found matching "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-[#c8ff00] hover:underline"
                >
                  Clear search and show all countries
                </button>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <Link to="/phone-numbers/all">
                <Button variant="outline">View Complete Coverage</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Why Choose National Numbers?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Globe className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Nationwide Reach</h3>
              <p className="text-gray-600">One number accessible from anywhere in the country, no geographic restrictions.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <TrendingUp className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Brand Recognition</h3>
              <p className="text-gray-600">Build a consistent national brand with a single memorable number.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Shield className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Full Compliance</h3>
              <p className="text-gray-600">Meet all national regulatory requirements and carrier guidelines.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Zap className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Instant Setup</h3>
              <p className="text-gray-600">Quick provisioning with immediate activation for your business.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <CheckCircle2 className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Easy Management</h3>
              <p className="text-gray-600">Simple configuration and routing through our self-service portal.</p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-shadow">
              <Users className="w-12 h-12 mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Scalable Solution</h3>
              <p className="text-gray-600">Handle high call volumes with unlimited capacity scaling.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Perfect For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>National Brands</h3>
              <p className="text-gray-600">Businesses serving customers across an entire country</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Customer Support</h3>
              <p className="text-gray-600">Centralized support lines accessible nationwide</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Marketing Campaigns</h3>
              <p className="text-gray-600">Single number for nationwide advertising and promotions</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] text-white">
            <h2 className="text-3xl font-bold mb-4">Ready for nationwide coverage?</h2>
            <p className="text-xl text-gray-300 mb-8">Get national numbers in {nationalCountries.length}+ countries today</p>
            <Button 
              size="lg"
              style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
              className="hover:opacity-90 font-semibold"
              onClick={() => window.location.href = '/contact'}
            >
              Get Started Now
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default NationalNumbers;